import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Clock, Mail, MapPin, Phone, Ruler, Users } from "lucide-react";
import { Eyebrow, FadeIn } from "./Reveal";

const DETAILS = [
  {
    icon: Phone,
    label: "Call Us",
    value: "+353 86 251 8252",
    href: "tel:+353862518252",
    testId: "contact-phone-link",
  },
  {
    icon: Mail,
    label: "Email",
    value: "info@theadventureislands.com",
    href: "mailto:info@theadventureislands.com",
    testId: "contact-email-link",
  },
  {
    icon: MapPin,
    label: "Find Us",
    value: "Collanmore Island Lodge, Clew Bay, Westport, Co. Mayo, Ireland",
  },
  {
    icon: Clock,
    label: "Open",
    value: "Daily 09:00 \u2013 18:00",
  },
];

const GROUP_TYPES = [
  "Hen Party",
  "Stag Party",
  "Corporate & Team Building",
  "School or Youth Group",
  "Family Adventure",
  "Tour Booking",
  "Something Else",
];

const SIZES = ["1\u20136", "7\u201312", "13\u201322", "22+"];

const inputCls =
  "w-full border-b border-white/20 bg-transparent px-0 py-3 text-sm text-ivory placeholder:text-mist/50 transition-colors duration-300 focus:border-gold focus:outline-none";
const labelCls = "text-[11px] font-medium uppercase tracking-[0.2em] text-mist";

export default function Contact() {
  const [sent, setSent] = useState(false);
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form).entries());
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/enquiries`, data);
      navigate("/thank-you", { state: { name: data.name || "" } });
    } catch (err) {
      setError(
        err.response && err.response.data && err.response.data.detail
          ? String(err.response.data.detail)
          : "Something went adrift \u2014 please try again or call us directly."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-navy-800 py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
        <div>
          <Eyebrow>Contact &amp; Booking</Eyebrow>
          <FadeIn delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl font-medium leading-[1.05] tracking-tight text-ivory sm:text-5xl lg:text-6xl">
              Reserve your <em className="italic text-gold">adventure</em>
            </h2>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="mt-8 max-w-md text-sm font-light leading-relaxed text-mist sm:text-base">
              Reserve your stay now with a bespoke group package &mdash; or book a tour straight
              from the pier. We'll build the perfect day for your group.
            </p>
          </FadeIn>

          <FadeIn delay={0.25}>
            <ul className="mt-12 space-y-8">
              {DETAILS.map((d) => (
                <li key={d.label} className="flex items-start gap-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-gold/40 text-gold">
                    <d.icon className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block text-[11px] uppercase tracking-[0.24em] text-mist">{d.label}</span>
                    {d.href ? (
                      <a
                        href={d.href}
                        data-testid={d.testId}
                        className="mt-1 block text-sm text-ivory transition-colors hover:text-gold sm:text-base"
                      >
                        {d.value}
                      </a>
                    ) : (
                      <span className="mt-1 block text-sm text-ivory sm:text-base">{d.value}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </FadeIn>

          <FadeIn delay={0.3}>
            <div className="mt-12 border-l-2 border-gold/60 bg-navy-900/60 p-6">
              <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-gold">Getting Here</p>
              <p className="mt-3 text-sm font-light leading-relaxed text-mist">
                By road: Rosmoney Pier is 12 minutes from Westport town. By sea: a 2-minute
                high-speed RIB crossing brings you to the island. The crossing is part of the
                experience.
              </p>
            </div>
          </FadeIn>
        </div>

        <FadeIn delay={0.2}>
          <div className="border border-gold/25 bg-navy-900/70 p-7 backdrop-blur-sm sm:p-10" data-testid="enquiry-form-card">
            {sent ? (
              <div className="flex min-h-[480px] flex-col items-center justify-center text-center" data-testid="enquiry-success">
                <CheckCircle2 className="h-14 w-14 text-gold" />
                <h3 className="mt-6 font-serif text-3xl text-ivory">
                  Thank you{name ? `, ${name.split(" ")[0]}` : ""}
                </h3>
                <p className="mt-4 max-w-sm text-sm font-light leading-relaxed text-mist">
                  Your enquiry is on its way across Clew Bay. We'll be in touch shortly to
                  build your perfect island adventure.
                </p>
                <button
                  onClick={() => setSent(false)}
                  className="mt-8 border border-gold/70 px-7 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-gold transition-colors hover:bg-gold-muted"
                >
                  Send Another Enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="enquiry-form" className="space-y-7">
                <div className="grid gap-7 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className={labelCls}>Name *</label>
                    <input id="name" name="name" required data-testid="enquiry-name-input" placeholder="Your name" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="email" className={labelCls}>Email *</label>
                    <input id="email" name="email" type="email" required data-testid="enquiry-email-input" placeholder="you@example.com" className={inputCls} />
                  </div>
                </div>
                <div className="grid gap-7 sm:grid-cols-2">
                  <div>
                    <label htmlFor="phone" className={labelCls}>Phone</label>
                    <input id="phone" name="phone" type="tel" data-testid="enquiry-phone-input" placeholder="+353 ..." className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="preferred_date" className={labelCls}>Preferred Date</label>
                    <input id="preferred_date" name="preferred_date" type="date" data-testid="enquiry-date-input" className={inputCls} />
                  </div>
                </div>
                <div className="grid gap-7 sm:grid-cols-2">
                  <div>
                    <label htmlFor="group_type" className={labelCls}>
                      <span className="inline-flex items-center gap-1.5"><Users className="h-3 w-3 text-gold" /> Group Type</span>
                    </label>
                    <select id="group_type" name="group_type" data-testid="enquiry-type-select" className={`${inputCls} appearance-none [&>option]:bg-navy-900`}>
                      {GROUP_TYPES.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="group_size" className={labelCls}>
                      <span className="inline-flex items-center gap-1.5"><Ruler className="h-3 w-3 text-gold" /> Group Size</span>
                    </label>
                    <select id="group_size" name="group_size" data-testid="enquiry-size-select" className={`${inputCls} appearance-none [&>option]:bg-navy-900`}>
                      {SIZES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="message" className={labelCls}>Tell Us About Your Adventure</label>
                  <textarea id="message" name="message" rows={4} data-testid="enquiry-message-input" placeholder="What brings you to Clew Bay?" className={`${inputCls} resize-none`} />
                </div>
                {error && (
                  <p className="text-sm text-red-300" data-testid="enquiry-error">{error}</p>
                )}
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="enquiry-submit-button"
                  className="w-full bg-gold py-4 text-[12px] font-semibold uppercase tracking-[0.24em] text-navy-800 transition-colors duration-300 hover:bg-gold-light disabled:cursor-wait disabled:opacity-70"
                >
                  {sending ? "Sending..." : "Send Enquiry"}
                </button>
              </form>
            )}
          </div>
        </FadeIn>
      </div>
    </section>
  );
}
